/**
 * The kitchen calls (ADR 0027) for either portal's client. The store and HO hosts return the same shapes; HO adds
 * `storeId` and has no printers or print jobs. Numbers arrive as `number | string` and are used as sent.
 */
import type { ApiProblem, components } from '@pos/api-client';

type Schemas = components['schemas'];
export type KitchenTicketSummary = Schemas['KitchenTicketSummaryDto'];
export type KitchenTicketDetail = Schemas['KitchenTicketDetailDto'];
export type KitchenTicketItem = Schemas['KitchenTicketItemDto'];
export type OrderKitchen = Schemas['OrderKitchenDto'];
export type KitchenSend = Schemas['KitchenSendDto'];
export type StationSummary = Schemas['StationSummaryDto'];
export type DeviceHealth = Schemas['DeviceHealthDto'];
export type PrinterStatus = Schemas['PrinterStatusDto'];
export type PrintJob = Schemas['PrintJobDto'];
export type RoutingPreview = Schemas['RoutingPreviewDto'];
export type StationRouting = Schemas['StationRoutingDto'];

export interface Paged<T> {
  items: T[];
  total: number;
  /** More match than the server counts (ADR 0127). */
  totalCapped?: boolean;
}

export interface TicketQuery {
  station?: string;
  status?: string;
  printStatus?: string;
  from?: string;
  to?: string;
  search?: string;
  storeId?: string;
  page: number;
  pageSize: number;
}

export interface JobQuery {
  status?: string;
  station?: string;
  device?: string;
  from?: string;
  to?: string;
  page: number;
  pageSize: number;
}

interface FetchResult {
  data?: unknown;
  error?: unknown;
  response: Response;
}

/** The part of an openapi-fetch client these calls use; the apps' clients are typed per host. */
interface LooseClient {
  GET(path: string, init?: object): Promise<FetchResult>;
  POST(path: string, init?: object): Promise<FetchResult>;
}

function unwrap<T>({ data, error, response }: FetchResult): T {
  if (!response.ok) {
    const problem = (error ?? {}) as ApiProblem;
    throw { ...problem, status: problem.status ?? response.status } satisfies ApiProblem;
  }
  return data as T;
}

const clean = (query: object) =>
  Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== ''));

const paged = <T>(page: { items: T[]; total: number | string; totalCapped?: boolean }): Paged<T> => ({ ...page, total: Number(page.total) });

export function kitchenApi(client: object) {
  const api = client as LooseClient;
  return {
    tickets: async (query: TicketQuery) =>
      paged(
        unwrap<{ items: KitchenTicketSummary[]; total: number | string }>(
          await api.GET('/api/v1/kitchen/tickets', { params: { query: clean(query) } }),
        ),
      ),
    ticket: async (id: string) =>
      unwrap<KitchenTicketDetail>(await api.GET('/api/v1/kitchen/tickets/{id}', { params: { path: { id } } })),
    orderTickets: async (orderId: string) =>
      unwrap<OrderKitchen>(await api.GET('/api/v1/kitchen/orders/{orderId}/tickets', { params: { path: { orderId } } })),
    stations: async (storeId?: string) =>
      unwrap<StationSummary[]>(await api.GET('/api/v1/kitchen/stations/summary', { params: { query: clean({ storeId }) } })),
    preview: async (query: { itemId: string; saleTypeId: string; storeId?: string }) =>
      unwrap<RoutingPreview>(await api.GET('/api/v1/kitchen/routing/preview', { params: { query: clean(query) } })),
    reprint: async (id: string) =>
      unwrap<KitchenTicketDetail>(await api.POST('/api/v1/kitchen/tickets/{id}/reprint', { params: { path: { id } } })),
    // Store only.
    jobs: async (query: JobQuery) =>
      paged(
        unwrap<{ items: PrintJob[]; total: number | string }>(
          await api.GET('/api/v1/printing/jobs', { params: { query: clean(query) } }),
        ),
      ),
    retryJob: async (id: string) =>
      unwrap<PrintJob>(await api.POST('/api/v1/printing/jobs/{id}/retry', { params: { path: { id } } })),
    printers: async () => unwrap<PrinterStatus[]>(await api.GET('/api/v1/printing/devices/status')),
    testPrint: async (id: string) =>
      unwrap<PrintJob>(await api.POST('/api/v1/printing/devices/{id}/test-print', { params: { path: { id } } })),
    routingRules: async (stationId?: string) =>
      unwrap<StationRouting[]>(
        await api.GET('/api/v1/devices/station-routing', { params: { query: clean({ stationId }) } }),
      ),
  };
}
